import { Mail, Sparkles, BookOpen } from 'lucide-react'

/** @typedef {'Sparkles' | 'Mail'} AgentCategoryIconName */

/**
 * Display metadata for pipeline categories. Keys match `ai_pipelines.category`.
 * @type {Record<string, { label: string; description: string; icon: typeof Sparkles; sort: number }>}
 */
export const AGENT_CATEGORY_META = {
  basecamp: {
    label: 'Basecamp',
    description: 'Digest and automation over synced Basecamp projects linked to clients.',
    icon: Sparkles,
    sort: 0,
  },
  outlook: {
    label: 'Outlook',
    description: 'Email digest pipeline — reads mailbox folders incrementally and synthesizes LLM summaries. No email content stored.',
    icon: Mail,
    sort: 1,
  },
  knowledge: {
    label: 'Knowledge',
    description: 'In-app Smart Agent on /knowledge — wiki + hybrid KB search with citations.',
    icon: BookOpen,
    sort: 2,
  },
}

/**
 * @param {string} categoryKey
 */
export function getAgentCategoryMeta(categoryKey) {
  const key = (categoryKey || 'basecamp').trim() || 'basecamp'
  return (
    AGENT_CATEGORY_META[key] ?? {
      label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
      description: 'AI agents in this category.',
      icon: Sparkles,
      sort: 99,
    }
  )
}

/**
 * @param {Array<{ category?: string | null; category_sort?: number | null; pipeline_sort?: number | null; [key: string]: unknown }>} pipelines
 * @param {Array<{ pipeline_id?: string | null; step_order?: number | null; [key: string]: unknown }>} agents
 * @returns {Array<{ categoryKey: string; meta: ReturnType<typeof getAgentCategoryMeta>; pipelines: Array<{ pipeline: Record<string, unknown>; agents: Record<string, unknown>[] }> }>}
 */
export function groupCatalogByCategory(pipelines, agents) {
  /** @type {Map<string, Array<Record<string, unknown>>>} */
  const agentsByPipeline = new Map()
  for (const a of agents || []) {
    const pid = a.pipeline_id || 'basecamp_daily_digest'
    if (!agentsByPipeline.has(pid)) agentsByPipeline.set(pid, [])
    agentsByPipeline.get(pid).push(a)
  }

  /** @type {Map<string, { categoryKey: string; meta: ReturnType<typeof getAgentCategoryMeta>; pipelines: Array<{ pipeline: Record<string, unknown>; agents: Record<string, unknown>[] }> }>} */
  const byCategory = new Map()

  const sortedPipes = [...(pipelines || [])].sort((a, b) => {
    const ca = a.category_sort ?? 0
    const cb = b.category_sort ?? 0
    if (ca !== cb) return ca - cb
    return (a.pipeline_sort ?? 0) - (b.pipeline_sort ?? 0)
  })

  for (const pipeline of sortedPipes) {
    const categoryKey = (pipeline.category || 'basecamp').trim() || 'basecamp'
    const meta = getAgentCategoryMeta(categoryKey)
    if (!byCategory.has(categoryKey)) {
      byCategory.set(categoryKey, { categoryKey, meta, pipelines: [] })
    }
    const list = agentsByPipeline.get(pipeline.id) || []
    list.sort((x, y) => (x.step_order ?? 99) - (y.step_order ?? 99))
    if (list.length === 0) continue
    byCategory.get(categoryKey).pipelines.push({ pipeline, agents: list })
  }

  return [...byCategory.values()]
    .filter((g) => g.pipelines.length > 0)
    .sort((a, b) => a.meta.sort - b.meta.sort)
}
